// =============================================================================
// FireISP 5.0 — Shared Client create/edit modal
// =============================================================================
// Used by ClientList (create + edit) and ClientDetail (edit). Talks to the
// typed API client (POST /clients, PUT /clients/:id) and reports success via
// onSaved so the caller can refresh its own queries.
// =============================================================================

import { useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import i18n from '@/i18n';
import { api } from '@/api/client';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface ClientFormBody {
  name: string;
  email?: string;
  phone?: string;
  client_type?: string;
  status?: string;
  tax_id?: string;
  curp?: string;
  address?: string;
  city?: string;
  state?: string;
  zip_code?: string;
  country?: string;
  locale?: string;
  latitude?: number;
  longitude?: number;
  credit_score?: number;
  risk_rating?: string;
}

// The network payload allows `null` for the nullable optional string fields
// (an explicit clear on edit — see handleSubmit) even though the controlled
// <input>'s `value` prop (bound to ClientFormBody, always a plain string in
// local form state) cannot.
type ClientSubmitBody = Omit<ClientFormBody, 'email' | 'phone' | 'tax_id' | 'curp' | 'address' | 'city' | 'state' | 'zip_code' | 'country'> & {
  email?: string | null;
  phone?: string | null;
  tax_id?: string | null;
  curp?: string | null;
  address?: string | null;
  city?: string | null;
  state?: string | null;
  zip_code?: string | null;
  country?: string | null;
};

/** Minimal shape needed to pre-fill the edit form (nullable to match API rows). */
export interface ClientFormInitial {
  id: number;
  name: string;
  email?: string | null;
  phone?: string | null;
  client_type?: string | null;
  status?: string | null;
  tax_id?: string | null;
  curp?: string | null;
  address?: string | null;
  city?: string | null;
  state?: string | null;
  zip_code?: string | null;
  country?: string | null;
  locale?: string | null;
  latitude?: number | string | null;
  longitude?: number | string | null;
  credit_score?: number | null;
  risk_rating?: string | null;
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

/**
 * Turn an API error into something to show an operator.
 *
 * The server's own message wins whenever it sends one — that is the part that
 * actually says WHY, and it is already localised server-side.
 *
 * When it does not, we fall back to a TRANSLATED generic rather than the
 * caller's English string. 93 call sites across 36 files passed literals like
 * 'Failed to load invoices', so a Spanish operator hit English mid-flow
 * whenever the server returned a bare status. Those literals are deliberately
 * ignored now (j47): the per-page wording is not worth ~180 translation keys,
 * and it was rarely actionable anyway — "Failed to load invoices" tells nobody
 * anything they cannot see from the empty invoice list.
 *
 * `fallback` is kept in the signature on purpose. Stripping it from 93 call
 * sites would be a mechanical churn diff large enough to bury the one-line
 * behaviour change, and the argument still documents intent at the call site.
 * It is used only when i18n has not initialised (tests, SSR-less bootstrap),
 * where t() would return the raw key and show an operator "common.error".
 */
export function extractApiError(err: unknown, fallback?: string): string {
  const e = err as { error?: { message?: string }; message?: string };
  const serverMessage = e?.error?.message || e?.message;
  if (serverMessage) return serverMessage;

  const generic = i18n.isInitialized ? i18n.t('common.error') : null;
  return generic && generic !== 'common.error' ? generic : (fallback ?? 'Something went wrong');
}

async function createClient(body: ClientSubmitBody): Promise<void> {
  const { error } = await api.POST('/clients', { body: body as never });
  if (error) throw new Error(extractApiError(error, 'Failed to create client'));
}

async function updateClient(id: number, body: ClientSubmitBody): Promise<void> {
  const { error } = await api.PUT('/clients/{id}', {
    params: { path: { id } },
    body: body as never,
  });
  if (error) throw new Error(extractApiError(error, 'Failed to update client'));
}

const CLIENT_TYPES = ['residential', 'business', 'corporate', 'government', 'wholesale'];
const STATUSES = ['active', 'inactive', 'suspended'];
const LOCALES = ['global', 'MX'];
const RISK_RATINGS = ['unrated', 'low', 'medium', 'high'];

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export interface ClientFormModalProps {
  mode: 'create' | 'edit';
  initial?: ClientFormInitial;
  onClose: () => void;
  onSaved: () => void;
}

export function ClientFormModal({ mode, initial, onClose, onSaved }: ClientFormModalProps) {
  const [form, setForm] = useState<ClientFormBody>({
    name: initial?.name ?? '',
    email: initial?.email ?? '',
    phone: initial?.phone ?? '',
    client_type: initial?.client_type ?? 'residential',
    status: initial?.status ?? 'active',
    tax_id: initial?.tax_id ?? '',
    curp: initial?.curp ?? '',
    address: initial?.address ?? '',
    city: initial?.city ?? '',
    state: initial?.state ?? '',
    zip_code: initial?.zip_code ?? '',
    country: initial?.country ?? '',
    locale: initial?.locale ?? 'global',
    risk_rating: initial?.risk_rating ?? 'unrated',
  });
  // Numeric fields are kept as strings while editing so the inputs can be empty.
  const [numeric, setNumeric] = useState({
    latitude: initial?.latitude != null ? String(initial.latitude) : '',
    longitude: initial?.longitude != null ? String(initial.longitude) : '',
    credit_score: initial?.credit_score != null ? String(initial.credit_score) : '',
  });
  const [error, setError] = useState('');

  const mutation = useMutation({
    mutationFn: (body: ClientSubmitBody) =>
      mode === 'create' ? createClient(body) : updateClient(initial!.id, body),
    onSuccess: () => {
      onSaved();
      onClose();
    },
    onError: (err: unknown) =>
      setError(err instanceof Error ? err.message : 'Failed to save client'),
  });

  function set<K extends keyof ClientFormBody>(key: K, value: ClientFormBody[K]) {
    setForm(prev => ({ ...prev, [key]: value }));
  }

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!form.name.trim()) {
      setError('Name is required.');
      return;
    }
    // Drop empty optional strings so they are not sent as "" (which can fail
    // email/enum validation) on create — there's nothing to clear yet. On
    // edit, a field the user just BLANKED (previously non-empty on `initial`,
    // now empty) must be sent as an explicit `null` — validate() and
    // BaseModel.update both treat an omitted key as "not part of this
    // PUT" (old value survives), while `null` is validation-safe for these
    // optional/nullable columns and actually clears them (see
    // frontend/.claude/agent-memory/fullstack-autonomous-engineer/
    // patch-diff-explicit-clear-vs-omit.md). Always send name and the select
    // values.
    const body: ClientSubmitBody = { name: form.name.trim() };
    (
      ['email', 'phone', 'tax_id', 'curp', 'address', 'city', 'state', 'zip_code', 'country'] as const
    ).forEach(k => {
      const v = (form[k] ?? '').trim();
      const original = (initial?.[k] ?? '').toString().trim();
      if (v) {
        body[k] = v;
      } else if (mode === 'edit' && original) {
        body[k] = null;
      }
    });
    body.client_type = form.client_type;
    body.status = form.status;
    body.locale = form.locale;
    body.risk_rating = form.risk_rating;
    // Parse numeric fields; only include valid finite numbers.
    const lat = parseFloat(numeric.latitude);
    const lng = parseFloat(numeric.longitude);
    const score = parseInt(numeric.credit_score, 10);
    if (numeric.latitude.trim() && Number.isFinite(lat)) body.latitude = lat;
    if (numeric.longitude.trim() && Number.isFinite(lng)) body.longitude = lng;
    if (numeric.credit_score.trim() && Number.isFinite(score)) body.credit_score = score;
    setError('');
    mutation.mutate(body);
  }

  const title = mode === 'create' ? 'New Client' : `Edit ${initial?.name ?? 'Client'}`;

  return (
    <div style={overlay} role="dialog" aria-modal="true" aria-label={title}>
      <div style={{ ...modalBox, width: 540, maxHeight: '90vh', overflowY: 'auto' }}>
        <h3 style={{ margin: '0 0 1rem' }}>{title}</h3>
        {error && <div style={errorBox}>{error}</div>}
        <form onSubmit={handleSubmit}>
          <label style={labelStyle}>Name *</label>
          <input
            style={inputStyle}
            type="text"
            value={form.name}
            onChange={e => set('name', e.target.value)}
            required
            autoFocus
          />

          <div style={twoCol}>
            <div>
              <label style={labelStyle}>Email</label>
              <input style={inputStyle} type="email" value={form.email} onChange={e => set('email', e.target.value)} />
            </div>
            <div>
              <label style={labelStyle}>Phone</label>
              <input style={inputStyle} type="text" value={form.phone} onChange={e => set('phone', e.target.value)} />
            </div>
          </div>

          <div style={twoCol}>
            <div>
              <label style={labelStyle}>Type</label>
              <select style={inputStyle} value={form.client_type} onChange={e => set('client_type', e.target.value)}>
                {CLIENT_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
              </select>
            </div>
            <div>
              <label style={labelStyle}>Status</label>
              <select style={inputStyle} value={form.status} onChange={e => set('status', e.target.value)}>
                {STATUSES.map(s => <option key={s} value={s}>{s}</option>)}
              </select>
            </div>
          </div>

          <div style={twoCol}>
            <div>
              <label style={labelStyle}>Tax ID</label>
              <input style={inputStyle} type="text" value={form.tax_id} onChange={e => set('tax_id', e.target.value)} />
            </div>
            <div>
              <label style={labelStyle}>Locale</label>
              <select style={inputStyle} value={form.locale} onChange={e => set('locale', e.target.value)}>
                {LOCALES.map(l => <option key={l} value={l}>{l}</option>)}
              </select>
            </div>
          </div>

          <label style={labelStyle}>Address</label>
          <input style={inputStyle} type="text" value={form.address} onChange={e => set('address', e.target.value)} />

          <div style={threeCol}>
            <div>
              <label style={labelStyle}>City</label>
              <input style={inputStyle} type="text" value={form.city} onChange={e => set('city', e.target.value)} />
            </div>
            <div>
              <label style={labelStyle}>State</label>
              <input style={inputStyle} type="text" value={form.state} onChange={e => set('state', e.target.value)} />
            </div>
            <div>
              <label style={labelStyle}>ZIP</label>
              <input style={inputStyle} type="text" value={form.zip_code} onChange={e => set('zip_code', e.target.value)} />
            </div>
          </div>

          <label style={labelStyle}>Country (ISO-2)</label>
          <input
            style={inputStyle}
            type="text"
            maxLength={2}
            placeholder="MX"
            value={form.country}
            onChange={e => set('country', e.target.value.toUpperCase())}
          />

          <div style={twoCol}>
            <div>
              <label style={labelStyle}>CURP</label>
              <input style={inputStyle} type="text" maxLength={18} value={form.curp}
                onChange={e => set('curp', e.target.value.toUpperCase())} />
            </div>
            <div>
              <label style={labelStyle}>Risk rating</label>
              <select style={inputStyle} value={form.risk_rating} onChange={e => set('risk_rating', e.target.value)}>
                {RISK_RATINGS.map(r => <option key={r} value={r}>{r}</option>)}
              </select>
            </div>
          </div>

          <div style={twoCol}>
            <div>
              <label style={labelStyle}>Credit score (0–1000)</label>
              <input style={inputStyle} type="number" min={0} max={1000} value={numeric.credit_score}
                onChange={e => setNumeric(p => ({ ...p, credit_score: e.target.value }))} />
            </div>
            <div />
          </div>

          <div style={twoCol}>
            <div>
              <label style={labelStyle}>Latitude</label>
              <input style={inputStyle} type="number" step="any" min={-90} max={90} value={numeric.latitude}
                onChange={e => setNumeric(p => ({ ...p, latitude: e.target.value }))} />
            </div>
            <div>
              <label style={labelStyle}>Longitude</label>
              <input style={inputStyle} type="number" step="any" min={-180} max={180} value={numeric.longitude}
                onChange={e => setNumeric(p => ({ ...p, longitude: e.target.value }))} />
            </div>
          </div>

          <div style={{ display: 'flex', gap: 8, marginTop: '1.25rem', justifyContent: 'flex-end' }}>
            <button type="button" onClick={onClose} style={cancelBtn}>Cancel</button>
            <button type="submit" style={submitBtn} disabled={mutation.isPending}>
              {mutation.isPending ? 'Saving…' : mode === 'create' ? 'Create' : 'Save'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Shared modal styles (exported for reuse by client-related modals)
// ---------------------------------------------------------------------------

export const overlay: React.CSSProperties = {
  position: 'fixed', inset: 0, background: 'rgba(0,0,0,.45)',
  display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 100,
};
export const modalBox: React.CSSProperties = {
  background: 'var(--bg-card)', borderRadius: 10, padding: '1.5rem',
  width: 420, maxWidth: '92vw', boxShadow: '0 8px 32px rgba(0,0,0,.18)',
};
export const errorBox: React.CSSProperties = {
  background: '#fee2e2', color: '#991b1b', padding: '8px 12px',
  borderRadius: 6, marginBottom: '0.75rem', fontSize: '0.85rem',
};
export const labelStyle: React.CSSProperties = {
  display: 'block', fontWeight: 600, fontSize: '0.8rem',
  color: 'var(--text-secondary)', marginBottom: 4, marginTop: 12,
};
export const inputStyle: React.CSSProperties = {
  width: '100%', boxSizing: 'border-box', padding: '7px 10px',
  border: '1px solid var(--input-border)', borderRadius: 6, fontSize: '0.875rem',
};
export const twoCol: React.CSSProperties = {
  display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10,
};
export const threeCol: React.CSSProperties = {
  display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 10,
};
export const submitBtn: React.CSSProperties = {
  background: 'var(--accent)', color: '#fff', border: 'none',
  padding: '7px 18px', borderRadius: 6, cursor: 'pointer',
  fontWeight: 600, fontSize: '0.875rem',
};
export const cancelBtn: React.CSSProperties = {
  background: 'var(--bg-card)', color: 'var(--text-secondary)', border: '1px solid var(--border-strong)',
  padding: '7px 18px', borderRadius: 6, cursor: 'pointer',
  fontWeight: 600, fontSize: '0.875rem',
};
export const dangerBtn: React.CSSProperties = {
  background: '#dc2626', color: '#fff', border: 'none',
  padding: '7px 18px', borderRadius: 6, cursor: 'pointer',
  fontWeight: 600, fontSize: '0.875rem',
};
